// src/Components/partners/PartnerAnalyticsTab.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { TrendingUp, Clock, Award, Smartphone, UserCog } from 'lucide-react';
import { partnersAPI } from '../../services/api';
import { Skeleton, Card, fmt, CHART_COLORS } from '../dashboard/design-system';

const RANGES = [
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: 'all', label: 'All time' },
];

export default function PartnerAnalyticsTab() {
  const [range, setRange] = useState('90d');
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await partnersAPI.getPartnerAnalytics(range);
      if (res.success) setData(res.data);
    } catch (err) {
      console.error('Failed to load partner analytics:', err);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Card key={i} className="p-5"><Skeleton className="h-16 w-full" /></Card>)}
        </div>
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }
  if (!data) return null;

  const funnelChartData = [
    { label: 'Submitted', value: data.funnel.submitted },
    { label: 'Approved', value: data.funnel.approved },
    { label: 'Rejected', value: data.funnel.rejected },
    { label: 'Pending', value: data.funnel.pending },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end gap-2">
        {RANGES.map((r) => (
          <button key={r.value} onClick={() => setRange(r.value)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition ${
              range === r.value ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}>
            {r.label}
          </button>
        ))}
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-5">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 bg-emerald-50">
            <TrendingUp size={18} className="text-emerald-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{data.funnel.approvalRate}%</p>
          <p className="text-sm text-gray-500 mt-0.5">Approval rate</p>
        </Card>
        <Card className="p-5">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 bg-sky-50">
            <Clock size={18} className="text-sky-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {data.avgDecisionDays != null ? `${data.avgDecisionDays}d` : '—'}
          </p>
          <p className="text-sm text-gray-500 mt-0.5">Avg time to decision</p>
        </Card>
        <Card className="p-5">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 bg-amber-50">
            <Award size={18} className="text-amber-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{fmt(data.totalClaimsThisMonth)}</p>
          <p className="text-sm text-gray-500 mt-0.5">Offer claims this month</p>
        </Card>
        <Card className="p-5">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 bg-violet-50">
            <Smartphone size={18} className="text-violet-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {data.sourceBreakdown.mobile_application}
            <span className="text-sm text-gray-400"> / {data.sourceBreakdown.manual_admin}</span>
          </p>
          <p className="text-sm text-gray-500 mt-0.5">App-sourced / manual</p>
        </Card>
      </div>

      {/* Funnel + source breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-5">Application Funnel</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={funnelChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {funnelChartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-5">Partners by Category</h3>
          {data.byCategory.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={data.byCategory} dataKey="value" nameKey="label" cx="50%" cy="50%" outerRadius={80} innerRadius={45}>
                  {data.byCategory.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Legend verticalAlign="bottom" height={30} iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-gray-400 text-center py-16">No approved partners yet</p>
          )}
        </Card>
      </div>

      {/* By country + top claims */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-5">Partners by Country</h3>
          {data.byCountry.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.byCountry} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis dataKey="label" type="category" width={90} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="value" radius={[0, 6, 6, 0]} fill={CHART_COLORS[1]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-gray-400 text-center py-16">No approved partners yet</p>
          )}
        </Card>

        <Card className="p-6">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-5">Top 5 Partners by Claims (this month)</h3>
          {data.topPartnersByClaims.length > 0 ? (
            <div className="space-y-2">
              {data.topPartnersByClaims.map((p, i) => (
                <div key={p.partnerId} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                  <span className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-xs font-bold text-gray-500">
                    {i + 1}
                  </span>
                  <span className="flex-1 text-sm font-medium text-gray-800 dark:text-gray-100">{p.name}</span>
                  <span className="text-sm font-bold text-red-600">{fmt(p.claims)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-16">
              No claim events yet — requires the mobile app's claim-event hookup (see deploy notes)
            </p>
          )}
        </Card>
      </div>
    </div>
  );
}